import * as React from "react";
import { NavLink } from "react-router-dom";
import { Icon, IconInput } from "../components/Icon";

/**
 * Bloom's quiet navigation treatment: a brand wash for the current page, with muted labels and
 * icons elsewhere. Shared surface tokens keep the rail consistent with the header in both themes.
 */

export interface NavItemProps {
    to: string;
    icon: IconInput;
    label: string;
    /** Marks the item active for any descendant route, not just an exact match. */
    matchNested?: boolean;
    onNavigate?: () => void;
}

export const NavItem: React.FC<NavItemProps> = ({ to, icon, label, matchNested = true, onNavigate }) => (
    <NavLink
        to={to}
        end={!matchNested}
        onClick={onNavigate}
        className={({ isActive }) =>
            `group flex items-center gap-2.5 rounded-btn px-3 py-2 no-underline transition-colors duration-200 ${
                isActive
                    ? "bg-primary-soft text-primary-text"
                    : "text-text hover:bg-surface-muted hover:text-text-strong"
            }`
        }
    >
        {({ isActive }) => (
            <>
                <Icon
                    icon={icon}
                    fixedWidth
                    className={`transition-colors duration-200 ${
                        isActive ? "text-primary-text" : "text-text-weak group-hover:text-text"
                    }`}
                />
                <span className={`flex-1 truncate text-[13px] ${isActive ? "font-semibold" : "font-medium"}`}>{label}</span>
            </>
        )}
    </NavLink>
);

export const SubNavItem: React.FC<NavItemProps> = ({ to, icon, label, onNavigate }) => (
    <NavLink
        to={to}
        onClick={onNavigate}
        className={({ isActive }) =>
            `flex items-center gap-2.5 rounded-btn px-3 py-2 text-[13px] no-underline transition-colors duration-200 ${
                isActive
                    ? "bg-primary-soft font-semibold text-primary-text"
                    : "text-text hover:bg-surface-muted hover:text-text-strong"
            }`
        }
    >
        <Icon icon={icon} fixedWidth className="text-xs" />
        <span className="truncate">{label}</span>
    </NavLink>
);

/** Section heading between groups of links. */
export const NavSection: React.FC<{ label: string }> = ({ label }) => (
    <div className="px-3 pb-2 pt-5 text-[10px] font-semibold uppercase tracking-[0.1em] text-text-weak">{label}</div>
);
